import 'ag-grid-enterprise/dist/styles/ag-grid.css';
import 'ag-grid-enterprise/dist/styles/ag-theme-material.css';
import 'ag-grid-enterprise';
import './WorkplaceGrid.scss';
import 'components/shared/SelectOption/LabeledSelect.scss';

import React, { Fragment, useEffect, useState } from 'react';
import {
  arrayOf,
  bool,
  func,
  number,
  object,
  oneOfType,
  shape,
  string
} from 'prop-types';

import { AgGridReact } from 'ag-grid-react';

import { getRowClassRules } from 'services/TableUtils';
import { workplaceRetirementAccountsUrl } from 'services/Externals';
import { FormattedDate } from 'components/shared/Text/Text';
import WorkplaceRetirementBalanceDisclosure
  from 'components/shared/WorkplaceRetirementBalanceDisclosure/WorkplaceRetirementBalanceDisclosure';
import { AAMBeaconButton } from 'components/shared/Beacon';

const WorkplaceGrid = ({
  workplaceAccounts,
  defaultColumnDef,
  autoGroupColumnDef,
  components,
  frameworkComponents,
  gridOptions,
  columnTypes,
  columnDefs,
  isDesktopLayout,
  isPrintLayout,
  asOfDate,
  isCustomerWebView
}) => {
  const [gridApi, setGridApi] = useState(null);
  const [columnApi, setColumnApi] = useState(null);

  const isRowMaster = () => {
    return false;
  };

  const getRowHeight = ({ node, data, api }) => {
    const { rowHeight } = api.getSizesForCurrentTheme();

    if (node && node.detail) {
      const allDetailRowHeight = (data.length - 1) * rowHeight;
      const gridSizes = api.getSizesForCurrentTheme();
      return allDetailRowHeight + gridSizes.headerHeight - 7;
    } else if (data) {
      // for row heights, since autoHeight only seems to work after some time.
      // height is multiple of 24; calculates how many lines there are in the account name given a line is 20 chars.
      const rowHeightMultiple = rowHeight / 2;
      const lineLength = isDesktopLayout ? 18.5 : 20;
      const longerLine = data.nameAndChecksum.length;
      const buttonHeight = isDesktopLayout ? 0 : 30;
      const finalHeight =
        rowHeightMultiple *
        (Math.round((longerLine + buttonHeight) / lineLength) + 1);
      return finalHeight < rowHeight ? rowHeight : finalHeight;
    }
  };

  // returns column defs depending on filter combinations
  const chooseDefs = () => {
    return columnDefs;
  };

  const onGridReady = ({ api, columnApi }) => {
    setGridApi(api);
    setColumnApi(columnApi);
  };

  const getRowClass = ({ node }) => {
    const { group, field } = node;
    return getRowClassRules(group, field);
  };

  if (isPrintLayout) {
    gridApi && gridApi.setDomLayout('print');
  } else {
    gridApi && gridApi.setDomLayout('autoHeight');
  }

  useEffect(() => {
    gridApi && gridApi.sizeColumnsToFit();
  }, [gridApi]);

  // resize columns based on print mode
  useEffect(() => {
    if (isPrintLayout) {
      columnApi && columnApi.autoSizeAllColumns();
    } else {
      gridApi && gridApi.sizeColumnsToFit();
    }
  }, [isPrintLayout, columnApi, gridApi]);

  return (
    <Fragment>
      <div className="grid-container workplace-retirement">
        <span className="workplace-grid accounts-page-as-of-date">
          As of <FormattedDate>{asOfDate}</FormattedDate>
        </span>
        <div className="ag-theme-material workplace-retirement">
          <AgGridReact
            columnDefs={chooseDefs()}
            defaultColDef={defaultColumnDef}
            defaultColGroupDef={defaultColumnDef}
            rowData={workplaceAccounts}
            frameworkComponents={frameworkComponents}
            columnTypes={columnTypes}
            isRowMaster={isRowMaster}
            autoGroupColumnDef={autoGroupColumnDef}
            components={components}
            getRowClass={getRowClass}
            onGridReady={onGridReady}
            getRowHeight={getRowHeight}
            {...gridOptions}
          />
          <WorkplaceRetirementBalanceDisclosure accounts={workplaceAccounts} />
        </div>
        <div className="workplace-retirement-buttons">
          <AAMBeaconButton
            id={'link-button-workplace-retirement'}
            linkHref={workplaceRetirementAccountsUrl}
            linkTarget="_blank"
            variant={'secondary'}
            scale={'normal'}
            disabled={isCustomerWebView}
          >
            Manage Workplace Retirement Accounts
          </AAMBeaconButton>
        </div>
      </div>
    </Fragment>
  );
};

WorkplaceGrid.defaultProps = {
  workplaceAccounts: [],
  autoGroupColumnDef: {},
  columnDefs: [],
  columnTypes: {},
  components: {},
  defaultColumnDef: {},
  frameworkComponents: {},
  gridOptions: {}
};

WorkplaceGrid.propTypes = {
  workplaceAccounts: arrayOf(
    shape({
      balance: number,
      dailyPriceChangeSum: oneOfType([number, string]),
      difference: number,
      isOwner: string.isRequired,
      nameAndChecksum: string.isRequired,
      workplaceRetirement: string,
      priceChangePercentSum: number,
      primaryOwner: string,
      quantitySum: number
    })
  ),
  columnDefs: arrayOf(
    shape({
      headerName: string,
      field: string.isRequired,
      rowGroup: bool,
      hide: bool,
      valueFormatter: func
    })
  ),
  defaultColumnDef: object.isRequired
};

export default WorkplaceGrid;
